import { useCallback, useEffect, useMemo, useState } from 'react';

import { pages } from '../components/Data/generalData';

const DEFAULT_ROWS_PER_PAGE = pages[1] ?? 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Shared controlled state for server-paginated invoice and quote tables.
 * Keeping it outside TableBasic makes the API query the single source of truth
 * for paging, ordering and quick search.
 */
const useDocumentListQuery = (filters, orderingFields) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState();
  const [quickSearchInput, setQuickSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const filtersKey = useMemo(() => JSON.stringify(filters ?? {}), [filters]);

  useEffect(() => {
    setPage(0);
  }, [filtersKey]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(quickSearchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [quickSearchInput]);

  const ordering = useMemo(() => {
    const backendField = orderBy ? orderingFields?.[orderBy] : undefined;

    if (!backendField) {
      return undefined;
    }

    return order === 'desc' ? `-${backendField}` : backendField;
  }, [order, orderBy, orderingFields]);

  const paginationParams = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      ordering,
      search: debouncedSearch || undefined,
    }),
    [debouncedSearch, ordering, page, rowsPerPage]
  );

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortChange = (field) => {
    if (!orderingFields?.[field]) {
      return;
    }

    setOrder((currentOrder) => (orderBy === field && currentOrder === 'asc' ? 'desc' : 'asc'));
    setOrderBy(field);
    setPage(0);
  };

  const ensureValidPage = useCallback((totalCount) => {
    const maximumPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);

    if (page > maximumPage) {
      setPage(maximumPage);
    }
  }, [page, rowsPerPage]);

  return {
    page,
    rowsPerPage,
    order,
    orderBy,
    quickSearchInput,
    setQuickSearchInput,
    paginationParams,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortChange,
    ensureValidPage,
  };
};

export default useDocumentListQuery;
